
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { useParams } from "react-router-dom";
import api from "../../api/axios";
import HTMLFlipBook from "react-pageflip";
import * as fabric from "fabric";

import {
 
  FiDownload,
  FiBookOpen,
  FiLoader,
  FiAlertCircle,
  FiChevronLeft,
  FiChevronRight,
  FiMaximize2,
  FiMinimize2,
} from "react-icons/fi";

import adssuLogo from "../../assets/adssu-logo.png";
import { downloadYearbookPDF } from "../../utils/yearbookPdf";
import "./flipbook.css";

const GREEN = "#17633d";

const parsePages = (rawPages) => {
  if (Array.isArray(rawPages)) return rawPages;

  if (typeof rawPages === "string") {
    try {
      const parsed = JSON.parse(rawPages);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      console.error("Failed to parse yearbook pages:", error);
    }
  }

  return [];
};

const parsePageJSON = (page) => {
  try {
    if (!page?.json) return null;

    return typeof page.json === "string"
      ? JSON.parse(page.json)
      : page.json;
  } catch {
    return null;
  }
};

const getPageText = (page) => {
  const json = parsePageJSON(page);

  return (json?.objects || [])
    .filter((obj) =>
      ["Textbox", "Text", "IText"].includes(obj.type)
    )
    .map((obj) => String(obj.text || "").trim())
    .filter(Boolean)
    .join("|")
    .toLowerCase();
};

const areDuplicatePages = (first, second) => {
  if (!first || !second || first.isBlank || second.isBlank) {
    return false;
  }

  if (
    first.id != null &&
    second.id != null &&
    String(first.id) === String(second.id)
  ) {
    return true;
  }

  const firstJSON = parsePageJSON(first);
  const secondJSON = parsePageJSON(second);

  if (
    firstJSON &&
    secondJSON &&
    JSON.stringify(firstJSON) === JSON.stringify(secondJSON)
  ) {
    return true;
  }

  const firstImage = first.preview || first.thumbnail || "";
  const secondImage = second.preview || second.thumbnail || "";

  if (!firstImage || firstImage !== secondImage) return false;

  if (!firstJSON || !secondJSON) return true;

  const firstText = getPageText(first);
  const secondText = getPageText(second);

  return (
    Boolean(firstText) &&
    firstText === secondText &&
    (
      first.type === "cover" ||
      second.type === "cover" ||
      first.title?.toLowerCase() === "cover" ||
      second.title?.toLowerCase() === "cover"
    )
  );
};

/*
 * Keep the front cover first and back cover last.
 * Add an empty page when needed to align the back cover.
 */
const prepareDisplayPages = (pages) => {
  if (!pages.length) return [];

  const normalized = [...pages];

  if (normalized.length > 1) {
    const last = normalized[normalized.length - 1];
    const previous = normalized[normalized.length - 2];

    if (
      areDuplicatePages(previous, last) ||
      areDuplicatePages(normalized[0], last)
    ) {
      normalized.pop();
    }
  }

  if (normalized.length <= 1) return normalized;

  const frontCover = normalized[0];
  const backCover = normalized[normalized.length - 1];
  const interior = normalized.slice(1, -1);

  const paddedInterior =
    interior.length % 2 !== 0
      ? [
          ...interior,
          {
            id: "__blank_before_back_cover",
            isBlank: true,
            orientation: frontCover.orientation || "portrait",
          },
        ]
      : interior;

  return [frontCover, ...paddedInterior, backCover];
};

/* =========================================================
   INDIVIDUAL BOOK PAGE
========================================================= */


const FlipbookPage = forwardRef(({ page, label }, ref) => {
  const canvasRef = useRef(null);

  // Every displayed page uses A4 portrait dimensions.
  const PAGE_WIDTH = 794;
  const PAGE_HEIGHT = 1123;

  useEffect(() => {
    const element = canvasRef.current;
    if (!element) return undefined;

    let disposed = false;
    let canvas = null;

    const renderPage = async () => {
      try {
        const json =
          typeof page?.json === "string"
            ? JSON.parse(page.json)
            : page?.json;

        if (!json) return;

        // Preserve the dimensions used by the original design.
        const sourceOrientation =
          page?.orientation === "landscape"
            ? "landscape"
            : "portrait";

        const sourceWidth =
          sourceOrientation === "landscape" ? 1123 : 794;

        const sourceHeight =
          sourceOrientation === "landscape" ? 794 : 1123;

        // Fit the complete design inside the portrait page.
        const scale = Math.min(
          PAGE_WIDTH / sourceWidth,
          PAGE_HEIGHT / sourceHeight
        );

        const offsetX =
          (PAGE_WIDTH - sourceWidth * scale) / 2;

        const offsetY =
          (PAGE_HEIGHT - sourceHeight * scale) / 2;

        canvas = new fabric.StaticCanvas(element, {
          width: PAGE_WIDTH,
          height: PAGE_HEIGHT,
          backgroundColor: "#ffffff",
          enableRetinaScaling: true,
          renderOnAddRemove: false,
        });

        await canvas.loadFromJSON(json);

        if (disposed) return;

        // Restore the required portrait viewport after loading.
        canvas.setDimensions({
          width: PAGE_WIDTH,
          height: PAGE_HEIGHT,
        });

        canvas.setViewportTransform([
          scale, 0,
          0, scale,
          offsetX, offsetY,
        ]);

        canvas.requestRenderAll();
      } catch (error) {
        if (!disposed) {
          console.error(
            "Failed to render flipbook page:",
            error
          );
        }
      }
    };

    renderPage();

    return () => {
      disposed = true;

      if (canvas) {
        canvas.dispose();
        canvas = null;
      }
    };
  }, [page?.json, page?.orientation]);

const fallbackImage =
  page?.imageUrl ||
  page?.preview ||
  page?.thumbnail ||
  "";

  return (
    <div
      ref={ref}
      className="flipbook-page flipbook-page--portrait"
    >
      <div className="flipbook-artwork">
        {page?.json ? (
          <canvas
            ref={canvasRef}
            className="flipbook-page-canvas"
            aria-label={label || "Yearbook page"}
            width={PAGE_WIDTH}
            height={PAGE_HEIGHT}
          />
        ) : fallbackImage ? (
          <img
            src={fallbackImage}
            alt={label || "Yearbook page"}
            draggable={false}
            className="flipbook-page-image"
          />
        ) : (
          <div className="flipbook-empty">
            This page could not be rendered.
          </div>
        )}
      </div>
    </div>
  );
});

FlipbookPage.displayName = "FlipbookPage";


/* =========================================================
   MAIN YEARBOOK VIEWER
========================================================= */

function FlipbookViewer() {
 
  const { id } = useParams();

  const flipBookRef = useRef(null);
  const [pages, setPages] = useState([]);
  const [yearbook, setYearbook] = useState(null);
  const [title, setTitle] = useState("Digital Yearbook");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [downloading, setDownloading] = useState(false);
const [currentPage, setCurrentPage] = useState(0);
const [isFullscreen, setIsFullscreen] = useState(false);

// Reader zoom: 60% to 150%
const [zoom, setZoom] = useState(1);

const zoomIn = () => {
  setZoom((current) =>
    Math.min(1.5, Number((current + 0.1).toFixed(2)))
  );
};

const zoomOut = () => {
  setZoom((current) =>
    Math.max(0.6, Number((current - 0.1).toFixed(2)))
  );
};

const resetZoom = () => setZoom(1);

  const [viewport, setViewport] = useState({
    width: typeof window !== "undefined" ? window.innerWidth : 1440,
    height: typeof window !== "undefined" ? window.innerHeight : 900,
  });

  const bookShellRef = useRef(null);
  const [stageSize, setStageSize] = useState({
    width: 1200,
    height: 700,
  });

  const displayPages = useMemo(
    () => prepareDisplayPages(pages),
    [pages]
  );

  const displayPageCount = displayPages.filter(
    (page) => !page.isBlank
  ).length;

  const isPortraitYearbook = true;

const isCompact = viewport.width <= 760;

// Use a two-page spread on wide screens.
// Switch to one page on mobile.
const useSinglePage = isCompact;


const pageDimensions = useMemo(() => {
  const pageWidth = 794;
  const pageHeight = 1123;

  // Available space inside the book stage.
  const availableWidth = Math.max(1, stageSize.width - 16);
  const availableHeight = Math.max(1, stageSize.height - 12);

  // Desktop: two pages. Mobile: one page.
  const pagesAcross = useSinglePage ? 1 : 2;

  // Scale proportionally. Never enlarge beyond the original size.
  const scale = Math.min(
    availableWidth / (pageWidth * pagesAcross),
    availableHeight / pageHeight,
    1
  );

  // Keep the original A4 portrait ratio.
  const width = Math.max(1, Math.floor(pageWidth * scale));
  const height = Math.max(1, Math.floor(pageHeight * scale));

  return { width, height };
}, [stageSize, useSinglePage]);


  useEffect(() => {
    const updateViewport = () => {
      setViewport({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    window.addEventListener("resize", updateViewport);
    window.addEventListener("orientationchange", updateViewport);

    return () => {
      window.removeEventListener("resize", updateViewport);
      window.removeEventListener("orientationchange", updateViewport);
    };
  }, []);

  useEffect(() => {
    const element = bookShellRef.current;
    if (!element) return undefined;

    const updateStage = () => {
      const rect = element.getBoundingClientRect();
      const styles = window.getComputedStyle(element);
      const horizontalPadding =
        parseFloat(styles.paddingLeft) + parseFloat(styles.paddingRight);
      const verticalPadding =
        parseFloat(styles.paddingTop) + parseFloat(styles.paddingBottom);

      setStageSize({
        width: Math.max(1, rect.width - horizontalPadding),
        height: Math.max(1, rect.height - verticalPadding),
      });
    };

    updateStage();

    if (typeof ResizeObserver !== "undefined") {
      const observer = new ResizeObserver(updateStage);
      observer.observe(element);

      return () => observer.disconnect();
    }

    window.addEventListener("resize", updateStage);

    return () => {
      window.removeEventListener("resize", updateStage);
    };
  }, []);
  const fetchYearbook = useCallback(async (yearbookId) => {
    if (!yearbookId) {
      setError("No yearbook was selected.");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError("");

      const response = await api.get(
        `/api/yearbooks/${yearbookId}`
      );

      const data = response.data?.yearbook || response.data;

      if (!data) {
        throw new Error("Yearbook was not found.");
      }

      const parsedPages = parsePages(data.pages);

      setYearbook(data);
      setTitle(
        data.title ||
          data.batch_name ||
          "Digital Yearbook"
      );
      setPages(parsedPages);
      setCurrentPage(0);
    } catch (err) {
      console.error("Failed to load yearbook:", err);

      setPages([]);
      setError(
        err.response?.data?.message ||
          err.message ||
          "Unable to load this yearbook."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchYearbook(id);
  }, [id, fetchYearbook]);

  const handleDownloadPDF = async () => {
    if (downloading) return;

    if (!pages.length) {
      window.alert(
        "This yearbook does not contain any pages to export."
      );
      return;
    }

    try {
      setDownloading(true);

      await new Promise((resolve) =>
        window.setTimeout(resolve, 100)
      );

      await downloadYearbookPDF(pages, title || "Yearbook");
    } catch (err) {
      console.error("Failed to generate PDF:", err);

      window.alert(
        err?.message ||
          "Failed to generate the PDF. Please try again."
      );
    } finally {
      setDownloading(false);
    }
  };

  const goPrevious = () => {
    flipBookRef.current?.pageFlip()?.flipPrev();
  };

  const goNext = () => {
    flipBookRef.current?.pageFlip()?.flipNext();
  };

  const handleFlip = (event) => {
    setCurrentPage(event.data ?? 0);
  };

  const toggleFullscreen = async () => {
    try {
      if (!document.fullscreenElement) {
        await document.documentElement.requestFullscreen?.();
        setIsFullscreen(true);
      } else {
        await document.exitFullscreen?.();
        setIsFullscreen(false);
      }
    } catch (err) {
      console.warn("Fullscreen is not available:", err);
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(Boolean(document.fullscreenElement));
    };

    document.addEventListener(
      "fullscreenchange",
      handleFullscreenChange
    );

    return () => {
      document.removeEventListener(
        "fullscreenchange",
        handleFullscreenChange
      );
    };
  }, []);

  const pageLabel = useMemo(() => {
    const page = displayPages[currentPage];

    if (!page || page.isBlank) return "Yearbook";

    return (
      page.title ||
      page.type ||
      `Page ${currentPage + 1}`
    );
  }, [displayPages, currentPage]);

  /* =========================
     LOADING STATE
  ========================= */

  if (loading) {
    return (
      <main className="yb-viewer">
        <div className="yb-status-card">
          <div className="yb-status-icon yb-spin">
            <FiLoader />
          </div>
          <h2>Preparing your yearbook</h2>
          <p>Loading pages and preparing the reading experience.</p>
        </div>
      </main>
    );
  }

  /* =========================
     ERROR / EMPTY STATES
  ========================= */

  if (error || !pages.length) {
    return (
      <main className="yb-viewer">
        <header className="yb-header">
         

          <div className="yb-brand">
            <img src={adssuLogo} alt="ADSSU logo" />
            <span>ADSSU <strong>Digital Yearbook</strong></span>
          </div>
        </header>

        <section className="yb-status-card">
          <div className="yb-status-icon">
            {error ? <FiAlertCircle /> : <FiBookOpen />}
          </div>

          <h2>
            {error ? "Unable to open yearbook" : "No published pages"}
          </h2>

          <p>
            {error ||
              "This yearbook does not contain any published pages."}
          </p>

          {error && (
            <button
              className="yb-primary-button"
              onClick={() => fetchYearbook(id)}
              type="button"
            >
              Try again
            </button>
          )}
        </section>
      </main>
    );
  }

  const isCoverPage =
    currentPage === 0 ||
    currentPage >= displayPages.length - 1;

  return (
    <main className="yb-viewer">
      {downloading && (
        <div className="yb-download-overlay">
          <div className="yb-download-card">
            <FiLoader className="yb-spin" />
            <h3>Preparing your PDF</h3>
            <p>Please wait while your yearbook is exported.</p>
            <div className="yb-progress">
              <span />
            </div>
          </div>
        </div>
      )}

      {/* COMPACT UNIVERSITY HEADER */}
      <header className="yb-header">
        <div className="yb-header-main">


          <div className="yb-brand">
            <img
              src={adssuLogo}
              alt="Agusan del Sur State University"
            />
            <div className="yb-brand-copy">
              <strong>ADSSU</strong>
              <span>Digital Yearbook</span>
            </div>
          </div>
        </div>

        <div className="yb-header-actions">
          <button
            className="yb-icon-button"
            onClick={toggleFullscreen}
            type="button"
            title={isFullscreen ? "Exit fullscreen" : "Fullscreen"}
            aria-label={isFullscreen ? "Exit fullscreen" : "Fullscreen"}
          >
            {isFullscreen ? <FiMinimize2 /> : <FiMaximize2 />}
          </button>

          {isCompact && (
  <div
    className="yb-zoom-controls"
    role="group"
    aria-label="Mobile yearbook zoom controls"
  >
    <button
      type="button"
      className="yb-zoom-button"
      onClick={zoomOut}
      disabled={zoom <= 0.6}
      aria-label="Zoom out"
    >
      −
    </button>

    <span className="yb-zoom-level">
      {Math.round(zoom * 100)}%
    </span>

    <button
      type="button"
      className="yb-zoom-button"
      onClick={zoomIn}
      disabled={zoom >= 1.5}
      aria-label="Zoom in"
    >
      +
    </button>

    <button
      type="button"
      className="yb-zoom-reset"
      onClick={resetZoom}
      disabled={zoom === 1}
      aria-label="Reset zoom"
    >
      ↺
    </button>
  </div>
)}

          <button
            className="yb-download-button"
            onClick={handleDownloadPDF}
            disabled={downloading}
            type="button"
          >
            {downloading ? (
              <FiLoader className="yb-spin" />
            ) : (
              <FiDownload />
            )}
            <span>{downloading ? "Exporting..." : "Download PDF"}</span>
          </button>
        </div>
      </header>

      {/* READING AREA */}
      <section
        className={`yb-reader ${
          isPortraitYearbook ? "yb-reader--portrait" : "yb-reader--landscape"
        }`}
      >
        {/* Minimal title area: only one displayed yearbook title */}
        <div className="yb-reader-heading">
          <div className="yb-heading-copy">
            <span className="yb-eyebrow">
              <FiBookOpen />
              UNIVERSITY ARCHIVE
            </span>

            <h1 title={title}>{title}</h1>

            {yearbook?.academic_year && (
              <p>Academic year {yearbook.academic_year}</p>
            )}
          </div>

          <div className="yb-total-pages">
            <strong>{displayPageCount}</strong>
            <span>{displayPageCount === 1 ? "PAGE" : "PAGES"}</span>
          </div>
        </div>

        {/* BOOK STAGE */}
        <div className="yb-stage">
          <div className="yb-stage-decoration" />


         <div
  className={`yb-book-shell ${
    zoom > 1 ? "yb-book-shell--zoomed" : ""
  }`}
  ref={bookShellRef}
>
            <div className="yb-book-shadow" />
<div
  className={`yb-book-instance ${
    useSinglePage
      ? "yb-book-instance--single"
      : "yb-book-instance--spread"
  }`}
style={{
  width: pageDimensions.width * (useSinglePage ? 1 : 2),
  height: pageDimensions.height,
  "--yb-book-zoom": isCompact ? zoom : 1,
}}
>

<HTMLFlipBook
  ref={flipBookRef}
  key={`${id}-${useSinglePage ? "single" : "spread"}-${pageDimensions.width}-${pageDimensions.height}`}
  width={pageDimensions.width}
  height={pageDimensions.height}
  size="fixed"
  minWidth={pageDimensions.width}
  maxWidth={pageDimensions.width}
  minHeight={pageDimensions.height}
  maxHeight={pageDimensions.height}
  autoSize={false}
  usePortrait={useSinglePage}
  showCover={true}
  startPage={Math.min(
    currentPage,
    Math.max(0, displayPages.length - 1)
  )}
  drawShadow={true}
  flippingTime={650}
  useMouseEvents={true}
  swipeDistance={25}
  clickEventForward={true}
  disableFlipByClick={false}
  maxShadowOpacity={0.22}
  mobileScrollSupport={true}
  showPageCorners={true}
  className="yb-flipbook"
  onFlip={handleFlip}
>
  {displayPages.map((page, index) => {
    const isCover =
      index === 0 || index === displayPages.length - 1;

    return (
      <div
        key={page.id ?? `page-${index}`}
        className={`page ${isCover ? "page--cover" : ""}`}
        data-density={isCover ? "hard" : "soft"}
      >
        {!page.isBlank && (
          <FlipbookPage
            page={page}
            label={
              index === 0
                ? "Front cover"
                : index === displayPages.length - 1
                  ? "Back cover"
                  : `Page ${index + 1}`
            }
          />
        )}
      </div>
    );
  })}
</HTMLFlipBook>

            </div>
          </div>

          {/* PAGE NAVIGATION */}
          <div className="yb-navigation">
            <button
              type="button"
              className="yb-nav-button"
              onClick={goPrevious}
              disabled={currentPage <= 0}
              aria-label="Previous page"
              title="Previous page"
            >
              <FiChevronLeft />
            </button>

            <div className="yb-page-indicator">
              <span className="yb-page-current">
                {Math.min(currentPage + 1, displayPages.length)}
              </span>
              <span className="yb-page-divider">/</span>
              <span>{displayPages.length}</span>
            </div>

            <button
              type="button"
              className="yb-nav-button"
              onClick={goNext}
              disabled={currentPage >= displayPages.length - 1}
              aria-label="Next page"
              title="Next page"
            >
              <FiChevronRight />
            </button>
          </div>
        </div>

        {/* SIMPLE READING FOOTER — NO REPEATED TITLE */}
        <footer className="yb-reader-footer">
          <div className="yb-reading-hint">
            <span className="yb-hint-dot" />
            <span>
              {isCompact
                ? "Swipe or tap the page corners to turn pages"
                : "Click or drag a page corner to turn the page"}
            </span>
          </div>

          <div className="yb-current-section">
            <span className="yb-section-label">CURRENT PAGE</span>
            <span className="yb-section-name">{pageLabel}</span>
          </div>
        </footer>
      </section>
    </main>
  );
}

export default FlipbookViewer;
